import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Container,
  Typography,
  IconButton,
  useTheme,
  alpha,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { MotionBox } from "@rusha/shared-theme";
import mermaid from "mermaid";
import { initMermaidFromTheme } from "../../utils/mermaid-init";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const MotionTypography = motion.create(Typography);

interface TemplateDiagram {
  id: string;
  name: string;
  category: string;
  description: string;
  mermaid: string;
}

const TEMPLATES: TemplateDiagram[] = [
  {
    id: "1",
    name: "Full-Stack Web App",
    category: "Web",
    description:
      "React frontend, FastAPI backend, PostgreSQL, and Redis. The standard production stack for data-intensive web applications.",
    mermaid: `flowchart LR
    subgraph Client
      direction TB
      Browser[Browser]
    end
    
    subgraph Rusha[Rusha Deployment]
      LB[Load Balancer]
      UI[React Static]
      API[FastAPI Server]
      DB[PostgreSQL]
      Cache[Redis]
    end
    
    Client --> LB
    LB --> UI
    LB --> API
    API --> DB
    API --> Cache
    
    style Client fill:#f3f4f6,stroke:#6b7280
    style Rusha fill:#dbeafe,stroke:#2563eb,stroke-width:2px
    style LB fill:#2563eb
    style UI fill:#2563eb
    style API fill:#2563eb
    style DB fill:#7c3aed
    style Cache fill:#f59e0b`,
  },
  {
    id: "2",
    name: "Data Lakehouse",
    category: "Data Engineering",
    description:
      "Airbyte for ingestion, dbt for transformations, Iceberg tables with Trino query engine. Build a modern data platform.",
    mermaid: `flowchart LR
    subgraph Sources
      API[REST APIs]
      DB[Databases]
      Files[Files]
    end
    
    subgraph Pipeline[Data Pipeline]
      Airbyte[Airbyte]
      Lake[(Iceberg Lake)]
      Trino[Trino SQL]
    end
    
    subgraph BI[Dashboards]
      Superset[Superset]
    end
    
    Sources --> Airbyte
    Airbyte --> Lake
    Lake --> Trino
    Trino --> Superset
    
    style Sources fill:#f3f4f6,stroke:#6b7280
    style Pipeline fill:#d1fae5,stroke:#10b981,stroke-width:2px
    style BI fill:#fee2e2,stroke:#ef4444,stroke-width:1px
    style Airbyte fill:#f59e0b
    style Lake fill:#7c3aed
    style Trino fill:#10b981
    style Superset fill:#ef4444`,
  },
  {
    id: "3",
    name: "Analytics Platform",
    category: "Analytics",
    description:
      "ClickHouse columnar database with Superset dashboards. Sub-second queries on billions of rows for real-time analytics.",
    mermaid: `flowchart LR
    subgraph Ingest[Ingestion]
      Kafka[Kafka]
      Batch[Batch]
    end
    
    subgraph Store[OLAP Store]
      CH[ClickHouse]
    end
    
    subgraph Visualize[Visualize]
      Superset[Superset]
    end
    
    Ingest --> Store
    Store --> Visualize
    
    style Ingest fill:#f3f4f6,stroke:#6b7280
    style Store fill:#dbeafe,stroke:#2563eb,stroke-width:2px
    style Visualize fill:#fee2e2,stroke:#ef4444
    style CH fill:#7c3aed
    style Superset fill:#ef4444`,
  },
];

export const TemplateShowcase: React.FC = () => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [svg, setSvg] = useState("");
  const [rendered, setRendered] = useState(false);

  const template = TEMPLATES[currentIndex];
  const isDark = theme.palette.mode === "dark";

  // Initialize mermaid with theme-aware colors
  useEffect(() => {
    const root = document.documentElement;

    // Set primary colors for mermaid
    root.style.setProperty("--mermaid-primary", theme.palette.primary.main);
    root.style.setProperty("--mermaid-secondary", theme.palette.secondary.main);
    root.style.setProperty("--mermaid-accent1", theme.palette.info.main);
    root.style.setProperty("--mermaid-accent2", theme.palette.warning.main);
    root.style.setProperty("--mermaid-accent3", theme.palette.error.main);

    initMermaidFromTheme(theme);
  }, [theme, isDark]);

  useEffect(() => {
    const renderDiagram = async () => {
      if (!containerRef.current) return;
      setRendered(false);

      try {
        const id = `template-${template.id}-${Date.now()}`;
        const { svg } = await mermaid.render(id, template.mermaid);
        setSvg(svg);
        setRendered(true);
      } catch {
        // Mermaid rendering failed silent
      }
    };

    renderDiagram();
  }, [template, theme.palette.mode]);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? TEMPLATES.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === TEMPLATES.length - 1 ? 0 : prev + 1));
  };

  return (
    <Box
      id="templates"
      component="section"
      sx={{
        py: 20,
        backgroundColor: alpha(theme.palette.secondary.main, 0.04),
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: `
            radial-gradient(circle at 30% 30%, ${alpha(theme.palette.primary.main, 0.06)} 0%, transparent 50%),
            radial-gradient(circle at 70% 70%, ${alpha(theme.palette.secondary.main, 0.06)} 0%, transparent 50%)
          `,
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <MotionBox
          sx={{ textAlign: "center", mb: 10 }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <MotionTypography
            variant="h2"
            sx={{
              mb: 3,
              fontWeight: 800,
              color: "text.primary",
              fontSize: { xs: "2rem", md: "2.5rem" },
            }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            What We Deploy
          </MotionTypography>

          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              fontWeight: 400,
              maxWidth: 550,
              mx: "auto",
              fontSize: "1.25rem",
            }}
          >
            Each template is production-ready with the right defaults. More
            templates coming as we scale.
          </Typography>
        </MotionBox>

        {/* Navigation dots */}
        <Box
          sx={{ display: "flex", justifyContent: "center", gap: 1.5, mb: 6 }}
        >
          {TEMPLATES.map((_, index) => (
            <Box
              key={index}
              onClick={() => setCurrentIndex(index)}
              sx={{
                width: index === currentIndex ? 32 : 8,
                height: 8,
                borderRadius: 4,
                bgcolor: index === currentIndex ? "primary.main" : "grey.300",
                cursor: "pointer",
                transition: "all 0.3s ease",
              }}
            />
          ))}
        </Box>

        {/* Diagram Card */}
        <Box sx={{ position: "relative", maxWidth: 900, mx: "auto" }}>
          <IconButton
            onClick={handlePrev}
            sx={{
              position: "absolute",
              left: { xs: -10, md: -56 },
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 10,
              bgcolor: "background.paper",
              boxShadow: 2,
              "&:hover": { bgcolor: "action.hover" },
            }}
            aria-label="Previous template"
          >
            <ChevronLeftIcon />
          </IconButton>

          <AnimatePresence mode="wait">
            <MotionBox
              key={template.id}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.25 }}
              sx={{
                border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                borderRadius: 4,
                p: { xs: 3, md: 5 },
                bgcolor: "background.paper",
                boxShadow: `0 10px 40px ${alpha(theme.palette.common.black, 0.08)}`,
                minHeight: 380,
              }}
            >
              {/* Template Info */}
              <Box sx={{ textAlign: "center", mb: 4 }}>
                <Typography
                  variant="overline"
                  sx={{
                    color: "text.secondary",
                    letterSpacing: "0.2em",
                    mb: 1,
                    display: "block",
                  }}
                >
                  {template.category}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    color: "text.primary",
                    fontWeight: 700,
                    mb: 2,
                  }}
                >
                  {template.name}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "text.secondary",
                    maxWidth: 600,
                    mx: "auto",
                    lineHeight: 1.7,
                  }}
                >
                  {template.description}
                </Typography>
              </Box>

              {/* Mermaid Diagram */}
              <Box
                ref={containerRef}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  overflowX: "auto",
                  py: 2,
                  "& svg": {
                    maxWidth: "100%",
                    height: "auto",
                  },
                }}
              >
                {rendered && svg && (
                  <Box dangerouslySetInnerHTML={{ __html: svg }} />
                )}
              </Box>

              {!rendered && (
                <Box
                  sx={{
                    height: 250,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography color="text.secondary">
                    Loading diagram...
                  </Typography>
                </Box>
              )}
            </MotionBox>
          </AnimatePresence>

          <IconButton
            onClick={handleNext}
            sx={{
              position: "absolute",
              right: { xs: -10, md: -56 },
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 10,
              bgcolor: "background.paper",
              boxShadow: 2,
              "&:hover": { bgcolor: "action.hover" },
            }}
            aria-label="Next template"
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Container>
    </Box>
  );
};

export default TemplateShowcase;
